import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import useMediaQuery from "@mui/material/useMediaQuery";

import { ColorMode, tokens } from "../theme/tokens";

/** What the person chose in the account menu. 'auto' follows the device setting. */
export type AppearancePreference = "light" | "dark" | "auto";

/** localStorage key; also read by the pre-paint script in public/index.html. */
export const APPEARANCE_STORAGE_KEY = "csipay.appearance";

export interface ColorModeContextValue {
  /** The stored choice: light, dark or auto (default). */
  preference: AppearancePreference;
  /** The mode actually rendered: the preference, or the device setting when 'auto'. */
  resolvedMode: ColorMode;
  /** Change and remember the choice (per browser). */
  setPreference: (preference: AppearancePreference) => void;
}

const isPreference = (value: unknown): value is AppearancePreference =>
  value === "light" || value === "dark" || value === "auto";

// Read synchronously so the first render already uses the right theme (the first
// render waits on MSAL init, so a post-paint read would flash the wrong colours).
// Storage can throw (Safari private mode, blocked storage), hence the try/catch.
const readStoredPreference = (): AppearancePreference => {
  try {
    const stored = window.localStorage.getItem(APPEARANCE_STORAGE_KEY);
    return isPreference(stored) ? stored : "auto";
  } catch {
    return "auto";
  }
};

export const ColorModeContext = createContext<
  ColorModeContextValue | undefined
>(undefined);

/**
 * Holds the Light / Dark / Auto appearance choice. Wrap the app once (index.tsx);
 * read it with `useColorMode()`.
 */
export function ColorModeProvider({ children }: { children: React.ReactNode }) {
  const [preference, setPreferenceState] =
    useState<AppearancePreference>(readStoredPreference);
  const prefersDark = useMediaQuery("(prefers-color-scheme: dark)", {
    noSsr: true,
  });

  const resolvedMode: ColorMode =
    preference === "auto" ? (prefersDark ? "dark" : "light") : preference;

  const setPreference = useCallback((next: AppearancePreference) => {
    setPreferenceState(next);
    try {
      window.localStorage.setItem(APPEARANCE_STORAGE_KEY, next);
    } catch {
      // Storage blocked: the choice still applies for this visit.
    }
  }, []);

  // Keep the inline styles set by the index.html pre-paint script in step with the
  // chosen mode; inline styles would otherwise beat CssBaseline's color-scheme.
  useEffect(() => {
    const root = document.documentElement;
    root.style.colorScheme = resolvedMode;
    root.style.backgroundColor = tokens[resolvedMode].page;
  }, [resolvedMode]);

  // Another tab changed the choice: follow it.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === APPEARANCE_STORAGE_KEY) {
        setPreferenceState(
          isPreference(event.newValue) ? event.newValue : "auto"
        );
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const value = useMemo(
    () => ({ preference, resolvedMode, setPreference }),
    [preference, resolvedMode, setPreference]
  );

  return (
    <ColorModeContext.Provider value={value}>
      {children}
    </ColorModeContext.Provider>
  );
}

export default ColorModeProvider;
